import React, { useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { Video } from '../types';
import { Platform } from '../types';
import { useOnClickOutside } from '../hooks/useClickOutside';

interface LibraryProps {
  savedVideos: Video[];
}

const Library: React.FC<LibraryProps> = ({ savedVideos }) => {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState('');
    const [isPlatformPopoverOpen, setPlatformPopoverOpen] = useState(false);
    const [isNotesPopoverOpen, setNotesPopoverOpen] = useState(false);
    const [selectedPlatforms, setSelectedPlatforms] = useState<Set<Platform>>(new Set());
    const [notesFilter, setNotesFilter] = useState<'all' | 'with' | 'without'>('all');

    const platformPopoverRef = useRef<HTMLDivElement>(null);
    const notesPopoverRef = useRef<HTMLDivElement>(null);

    useOnClickOutside(platformPopoverRef, () => setPlatformPopoverOpen(false));
    useOnClickOutside(notesPopoverRef, () => setNotesPopoverOpen(false));
    
    const platformOptions = Object.values(Platform);

    const handlePlatformChange = (platform: Platform) => {
        const newSelection = new Set(selectedPlatforms);
        if (newSelection.has(platform)) {
            newSelection.delete(platform);
        } else {
            newSelection.add(platform);
        }
        setSelectedPlatforms(newSelection);
    };

    const clearPlatformSelection = () => {
        setSelectedPlatforms(new Set());
    };

    const filteredVideos = useMemo(() => {
        return savedVideos.filter(video => {
            const matchesSearch = video.title.toLowerCase().includes(searchTerm.toLowerCase()) || video.creatorName.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesPlatform = selectedPlatforms.size === 0 || selectedPlatforms.has(video.platform);
            const matchesNotes = notesFilter === 'all' || (notesFilter === 'with' ? !!video.notes : !video.notes);
            return matchesSearch && matchesPlatform && matchesNotes;
        });
    }, [savedVideos, searchTerm, selectedPlatforms, notesFilter]);
    
    const getNotesFilterLabel = () => {
        if(notesFilter === 'with') return t('library.filterNotesWith');
        if(notesFilter === 'without') return t('library.filterNotesWithout');
        return t('library.filterNotes');
    }

    return (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm">
            <header className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
                <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">{t('library.title')}</h1>
                <div className="mt-4 relative">
                    <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">search</span>
                    <input 
                        className="w-full rounded-lg border-0 bg-gray-100 dark:bg-gray-700 py-2.5 pl-10 pr-4 text-base text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:ring-2 focus:ring-[var(--primary-color)] focus:ring-inset" 
                        placeholder={t('library.searchPlaceholder')} 
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
                    {/* Platform Filter */}
                    <div className="relative" ref={platformPopoverRef}>
                         <button onClick={() => setPlatformPopoverOpen(!isPlatformPopoverOpen)} className={`flex h-9 shrink-0 items-center justify-center gap-x-1.5 rounded-lg px-3 text-sm transition-colors ${selectedPlatforms.size > 0 ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300' : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'}`}>
                            <span className="material-symbols-outlined text-lg">{selectedPlatforms.size > 0 ? 'filter_list' : 'cell_tower'}</span>
                            <p className="font-medium">{selectedPlatforms.size > 0 ? t('library.filterPlatformWithCount', {count: selectedPlatforms.size}) : t('library.filterPlatform')}</p>
                            <span className="material-symbols-outlined text-xl">expand_more</span>
                        </button>
                        {isPlatformPopoverOpen && (
                            <div className="absolute top-full mt-2 w-64 z-10 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg p-4">
                                <div className="grid grid-cols-2 gap-2">
                                    {platformOptions.map(platform => (
                                        <label key={platform} className="flex items-center gap-2 p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                className="h-4 w-4 rounded text-[var(--primary-color)] focus:ring-[var(--primary-color)] border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                                                checked={selectedPlatforms.has(platform)}
                                                onChange={() => handlePlatformChange(platform)}
                                            />
                                            <span className="text-sm text-gray-800 dark:text-gray-200">{t(`platform.${platform}`)}</span>
                                        </label>
                                    ))}
                                </div>
                                <div className="mt-4 flex justify-between gap-2 border-t border-gray-200 dark:border-gray-700 pt-3">
                                    <button onClick={clearPlatformSelection} className="px-4 py-1.5 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600">{t('actions.clear')}</button>
                                    <button onClick={() => setPlatformPopoverOpen(false)} className="px-4 py-1.5 rounded-md text-sm font-medium text-white bg-[var(--primary-color)] hover:bg-opacity-90">{t('actions.apply')}</button>
                                </div>
                            </div>
                        )}
                    </div>
                     {/* Notes Filter */}
                    <div className="relative" ref={notesPopoverRef}>
                        <button onClick={() => setNotesPopoverOpen(!isNotesPopoverOpen)} className={`flex h-9 shrink-0 items-center justify-center gap-x-1.5 rounded-lg px-3 text-sm transition-colors ${notesFilter !== 'all' ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300' : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'}`}>
                             <span className="material-symbols-outlined text-lg">{notesFilter !== 'all' ? 'filter_list' : 'description'}</span>
                            <p className="font-medium">{getNotesFilterLabel()}</p>
                            <span className="material-symbols-outlined text-xl">expand_more</span>
                        </button>
                         {isNotesPopoverOpen && (
                            <div className="absolute top-full mt-2 w-48 z-10 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg p-2">
                                <button onClick={() => { setNotesFilter('all'); setNotesPopoverOpen(false); }} className="w-full text-left p-2 rounded-md text-sm text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">{t('library.filterNotesAll')}</button>
                                <button onClick={() => { setNotesFilter('with'); setNotesPopoverOpen(false); }} className="w-full text-left p-2 rounded-md text-sm text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">{t('library.filterNotesWith')}</button>
                                <button onClick={() => { setNotesFilter('without'); setNotesPopoverOpen(false); }} className="w-full text-left p-2 rounded-md text-sm text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">{t('library.filterNotesWithout')}</button>
                            </div>
                         )}
                    </div>
                    <button className="flex h-9 shrink-0 items-center justify-center gap-x-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 px-3 text-sm hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                        <span className="material-symbols-outlined text-lg text-gray-600 dark:text-gray-300">tune</span>
                        <p className="text-gray-800 dark:text-gray-200 font-medium">{t('library.filterMore')}</p>
                    </button>
                </div>
            </header>
            
            <main className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 p-4">
                {filteredVideos.map((video) => (
                    <div key={video.id} className="w-full aspect-[9/16] rounded-lg overflow-hidden group relative cursor-pointer">
                        <img alt={video.title} className="absolute inset-0 size-full object-cover transition-transform duration-300 group-hover:scale-110" src={video.thumbnailUrl} />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                        <div className="absolute bottom-2 left-2 text-white">
                            <p className="text-sm font-semibold">{video.creatorName}</p>
                            <p className="text-xs">{t(`platform.${video.platform}`)}</p>
                        </div>
                    </div>
                ))}
            </main>
        </div>
    );
};

export default Library;